/**
 * crossTabNavigation.ts - Cross-Tab Navigation Service
 *
 * Provides navigation helpers for seamless movement between
 * Oracle Insights, Strategy Workspace, Business Graph, and Analysis Report tabs.
 *
 * Per wobbly-nibbling-chipmunk.md Phase 5
 * Per REASONING_ORACLE_PROTOTYPE_SELECTION_v3.md Section 12.3
 */

import type { CenterTab } from '@contexts/TabNavigationContext'

// ============================================================================
// Navigation Context Types
// ============================================================================

/**
 * Context for navigating to Oracle tab with specific filters/highlights
 */
export interface OracleNavigationContext {
  /** Filter chains by pillar name */
  filterByPillar?: string
  /** Filter chains by node ID */
  filterByNode?: string
  /** Highlight a specific chain by index */
  highlightChainIndex?: number
  /** Source of navigation (for analytics/breadcrumbs) */
  source?: 'workspace' | 'report' | 'graph'
}

/**
 * Context for navigating to Strategy Workspace with specific focus
 */
export interface WorkspaceNavigationContext {
  /** Focus on a specific MECE pillar */
  focusPillar?: string
  /** Focus on Strategy Map section */
  focusSection?: 'mece' | 'strategy-map'
  /** Highlight specific objective in Strategy Map */
  highlightObjective?: string
  /** Source of navigation */
  source?: 'oracle' | 'report' | 'graph'
}

/**
 * Context for navigating to Business Graph with specific focus
 */
export interface GraphNavigationContext {
  /** Select a specific node by ID */
  selectNodeId?: string
  /** Highlight a path of node IDs */
  highlightPath?: string[]
  /** Center view on specific node */
  centerOnNode?: string
  /** Source of navigation */
  source?: 'oracle' | 'workspace' | 'report'
}

/**
 * Context for navigating to Analysis Report with specific focus
 */
export interface ReportNavigationContext {
  /** Scroll to specific section */
  scrollToSection?: 'synthesis' | 'methodology' | 'recommendations'
  /** Source of navigation */
  source?: 'oracle' | 'workspace' | 'graph'
}

/**
 * Union type for all navigation contexts
 */
export type NavigationContext =
  | { tab: 'oracle'; context: OracleNavigationContext }
  | { tab: 'workspace'; context: WorkspaceNavigationContext }
  | { tab: 'graph'; context: GraphNavigationContext }
  | { tab: 'report'; context: ReportNavigationContext }

// ============================================================================
// Navigation State (stored in session for cross-tab communication)
// ============================================================================

const NAVIGATION_CONTEXT_KEY = 'omap_navigation_context'

/**
 * Store navigation context for the target tab to read
 */
export function setNavigationContext(context: NavigationContext): void {
  try {
    sessionStorage.setItem(NAVIGATION_CONTEXT_KEY, JSON.stringify(context))
  } catch (e) {
    console.warn('[CrossTabNav] Failed to store navigation context:', e)
  }
}

/**
 * Read and clear navigation context (called by target tab on mount)
 */
export function getNavigationContext(): NavigationContext | null {
  try {
    const stored = sessionStorage.getItem(NAVIGATION_CONTEXT_KEY)
    if (stored) {
      sessionStorage.removeItem(NAVIGATION_CONTEXT_KEY)
      return JSON.parse(stored) as NavigationContext
    }
  } catch (e) {
    console.warn('[CrossTabNav] Failed to read navigation context:', e)
  }
  return null
}

/**
 * Clear any pending navigation context
 */
export function clearNavigationContext(): void {
  try {
    sessionStorage.removeItem(NAVIGATION_CONTEXT_KEY)
  } catch (e) {
    // Ignore
  }
}

// ============================================================================
// Navigation Helper Functions
// ============================================================================

/**
 * Navigate to Oracle tab with optional context
 */
export function createOracleNavigation(
  navigateToTab: (tab: CenterTab) => void,
  context?: OracleNavigationContext
): void {
  if (context) {
    setNavigationContext({ tab: 'oracle', context })
  }
  navigateToTab('oracle')
}

/**
 * Navigate to Oracle filtered by pillar
 * Called from Strategy Workspace "Why this pillar?" links
 */
export function navigateToOracleForPillar(
  navigateToTab: (tab: CenterTab) => void,
  pillarName: string
): void {
  createOracleNavigation(navigateToTab, {
    filterByPillar: pillarName,
    source: 'workspace',
  })
}

/**
 * Navigate to Oracle filtered by node
 * Called from Business Graph node context menu
 */
export function navigateToOracleForNode(
  navigateToTab: (tab: CenterTab) => void,
  nodeId: string
): void {
  createOracleNavigation(navigateToTab, {
    filterByNode: nodeId,
    source: 'graph',
  })
}

/**
 * Navigate to Strategy Workspace with optional context
 */
export function createWorkspaceNavigation(
  navigateToTab: (tab: CenterTab) => void,
  context?: WorkspaceNavigationContext
): void {
  if (context) {
    setNavigationContext({ tab: 'workspace', context })
  }
  navigateToTab('workspace')
}

/**
 * Navigate to Workspace focusing on a specific pillar
 * Called from Oracle "View Tactics for this Pillar" buttons
 */
export function navigateToWorkspaceForPillar(
  navigateToTab: (tab: CenterTab) => void,
  pillarName: string
): void {
  createWorkspaceNavigation(navigateToTab, {
    focusPillar: pillarName,
    focusSection: 'mece',
    source: 'oracle',
  })
}

/**
 * Navigate to Workspace focusing on Strategy Map
 * Called from Oracle "View Strategy Map" buttons
 */
export function navigateToWorkspaceStrategyMap(
  navigateToTab: (tab: CenterTab) => void,
  objectiveId?: string
): void {
  createWorkspaceNavigation(navigateToTab, {
    focusSection: 'strategy-map',
    highlightObjective: objectiveId,
    source: 'oracle',
  })
}

/**
 * Navigate to Business Graph with optional context
 */
export function createGraphNavigation(
  navigateToTab: (tab: CenterTab) => void,
  context?: GraphNavigationContext
): void {
  if (context) {
    setNavigationContext({ tab: 'graph', context })
  }
  navigateToTab('graph')
}

/**
 * Navigate to Graph and select a specific node
 * Called from Oracle when clicking on a matched node
 */
export function navigateToGraphForNode(
  navigateToTab: (tab: CenterTab) => void,
  nodeId: string
): void {
  createGraphNavigation(navigateToTab, {
    selectNodeId: nodeId,
    centerOnNode: nodeId,
    source: 'oracle',
  })
}

/**
 * Navigate to Graph and highlight a causal path
 * Called from Oracle to show inference chain in graph
 */
export function navigateToGraphForPath(
  navigateToTab: (tab: CenterTab) => void,
  nodeIds: string[]
): void {
  createGraphNavigation(navigateToTab, {
    highlightPath: nodeIds,
    centerOnNode: nodeIds[0],
    source: 'oracle',
  })
}

/**
 * Navigate to Analysis Report with optional context
 */
export function createReportNavigation(
  navigateToTab: (tab: CenterTab) => void,
  context?: ReportNavigationContext
): void {
  if (context) {
    setNavigationContext({ tab: 'report', context })
  }
  navigateToTab('report')
}

/**
 * Navigate to Analysis Report synthesis section
 * Called from Oracle "View Analysis Report" buttons
 */
export function navigateToReportSynthesis(
  navigateToTab: (tab: CenterTab) => void
): void {
  createReportNavigation(navigateToTab, {
    scrollToSection: 'synthesis',
    source: 'oracle',
  })
}

// ============================================================================
// React Hook for consuming navigation context
// ============================================================================

/**
 * Hook to read navigation context on tab mount
 * Usage:
 *   const navContext = useNavigationContext('oracle')
 *   // navContext will be OracleNavigationContext | null
 */
export function useNavigationContextOnMount<T extends CenterTab>(
  expectedTab: T
): T extends 'oracle'
  ? OracleNavigationContext | null
  : T extends 'workspace'
    ? WorkspaceNavigationContext | null
    : T extends 'graph'
      ? GraphNavigationContext | null
      : T extends 'report'
        ? ReportNavigationContext | null
        : null {
  const context = getNavigationContext()

  if (context && context.tab === expectedTab) {
    return context.context as any
  }

  return null as any
}

// ============================================================================
// Breadcrumb / Back Navigation Support
// ============================================================================

const NAVIGATION_HISTORY_KEY = 'omap_navigation_history'
const MAX_HISTORY_LENGTH = 10

interface NavigationHistoryEntry {
  tab: CenterTab
  timestamp: number
  context?: NavigationContext
}

/**
 * Push current location to navigation history
 */
export function pushNavigationHistory(tab: CenterTab, context?: NavigationContext): void {
  try {
    const stored = sessionStorage.getItem(NAVIGATION_HISTORY_KEY)
    const history: NavigationHistoryEntry[] = stored ? JSON.parse(stored) : []

    history.push({
      tab,
      timestamp: Date.now(),
      context,
    })

    // Keep only recent entries
    if (history.length > MAX_HISTORY_LENGTH) {
      history.shift()
    }

    sessionStorage.setItem(NAVIGATION_HISTORY_KEY, JSON.stringify(history))
  } catch (e) {
    // Ignore
  }
}

/**
 * Get the previous tab for "back" navigation
 */
export function getPreviousTab(): CenterTab | null {
  try {
    const stored = sessionStorage.getItem(NAVIGATION_HISTORY_KEY)
    if (stored) {
      const history: NavigationHistoryEntry[] = JSON.parse(stored)
      if (history.length >= 2) {
        // Return second-to-last entry (current tab is last)
        return history[history.length - 2].tab
      }
    }
  } catch (e) {
    // Ignore
  }
  return null
}

/**
 * Clear navigation history
 */
export function clearNavigationHistory(): void {
  try {
    sessionStorage.removeItem(NAVIGATION_HISTORY_KEY)
  } catch (e) {
    // Ignore
  }
}
